import { downloadCSV } from 'react-admin';
import jsonExport from 'jsonexport/dist';
import {
  RateSettingsDisplayHelper,
  type RateClassificationT,
} from 'utils/displayHelpers/rate-settings';

export function mapRateSettingToRow(
  aRateSetting: any,
  isMarket: boolean,
  settingsNestedOperator: string,
): Record<string, any> {
  const {
    id,
    location_position_rate_setting_active,
    market_location_type_position_rate_setting_active,
    market_position_rate_setting_active,
    state_location_type_position_rate_setting_active,
    state_position_rate_setting_active,
    position,
    specialty,
    settings,
    non_blended_settings_charge_rate,
    blended_settings_charge_rate,
  } = aRateSetting;

  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({ settings });
  const applicableChargeRateCents = rateSettingsDisplayHelper.getActiveChargeRateCents(
    settingsNestedOperator as RateClassificationT,
  );

  const selectedRateSettings =
    rateSettingsDisplayHelper.getSettingsForClassification(settingsNestedOperator);
  const { ten99, w2 } = settings;

  const getRateSettings = (rateSettings) => {
    return rateSettings?.use_blended_rate
      ? rateSettings?.blended_rates
      : rateSettings?.non_blended_rates;
  };

  const getClassificationKey = (settingsNestedOperator: string) => {
    return settingsNestedOperator.replace('.', '_');
  };

  const ten99Rates = getRateSettings(ten99);

  // w2.local and w2.travel share the same settings configuration
  const w2Rates = getRateSettings(w2.local);

  let rateSettingToExport = {
    id,
    Active: '',
  };

  // Add state/market/location level specific fields
  if (id.startsWith('state_location_type')) {
    rateSettingToExport = {
      ...rateSettingToExport,
      Active: state_location_type_position_rate_setting_active,
    };
  } else if (id.startsWith('state')) {
    rateSettingToExport = {
      ...rateSettingToExport,
      Active: state_position_rate_setting_active,
    };
  } else if (id.startsWith('market_location_type')) {
    rateSettingToExport = {
      ...rateSettingToExport,
      Active: market_location_type_position_rate_setting_active,
    };
  } else if (id.startsWith('market')) {
    rateSettingToExport = {
      ...rateSettingToExport,
      Active: market_position_rate_setting_active,
    };
  } else {
    rateSettingToExport = {
      ...rateSettingToExport,
      Active: location_position_rate_setting_active,
    };
  }

  // Add all the rest of the fields.
  Object.assign(rateSettingToExport, {
    'Active for Job': settings?.can_post_job,
    'Active for Assignment': settings?.can_post_assignment,
    'position.display_name': position?.display_name,
    'position.id': position?.id,
    'specialty.label': specialty?.label,
    'specialty.id': specialty?.id,
    'Filtered charge_rate_cents': applicableChargeRateCents,
    ...(isMarket && {
      '8hr Charge Rate':
        non_blended_settings_charge_rate?.[
          `${getClassificationKey(settingsNestedOperator)}_without_location_type_markup`
        ],
      '12hr Charge Rate':
        blended_settings_charge_rate?.[
          `${getClassificationKey(settingsNestedOperator)}_without_location_type_markup`
        ],
    }),
    [`8hr ${isMarket ? 'Markup' : ''} Charge Rate`]: isMarket
      ? non_blended_settings_charge_rate?.[
          `${getClassificationKey(settingsNestedOperator)}_with_location_type_markup`
        ]
      : selectedRateSettings?.charge_rate_cents,
    [`12hr ${isMarket ? 'Markup' : ''} Charge Rate`]: isMarket
      ? blended_settings_charge_rate?.[
          `${getClassificationKey(settingsNestedOperator)}_with_location_type_markup`
        ]
      : Math.round(
          selectedRateSettings?.charge_rate_cents *
            (1 + selectedRateSettings?.blended_rates?.blended_rate_markup),
        ),
    'settings.ten99.margin_percent_1099': settings?.ten99?.margin_percent_1099,
    // 1099 base/markup settings
    '1099 Base Charge Rate': ten99?.base_charge_rate_cents,
    'Base Margin Percent (1099)': ten99?.base_margin_percent,
    'Invoicing Markup (1099)': ten99?.invoicing_markup,
    'Misc Markup (1099)': ten99?.misc_markup,
    // W2 base/markup settings
    'Operational Markup (W2 Local)': w2?.local?.operational_markup,
    'Base Margin Percent (W2 TCV)': w2?.travel?.base_margin_percent_w2_tcv,
    'Base Margin Percent (W2 Travel)': w2?.travel?.base_margin_percent_w2_travel,
    'settings.charge_on_call_hourly_rate_cents': settings?.charge_on_call_hourly_rate_cents,
    'settings.on_call_fee_percent': settings?.on_call_fee_percent,
    'Rate Structure': selectedRateSettings?.use_blended_rate ? '12hr' : '8hr',
    // 1099 Charge settings
    '1099 charge_max_daily_regular_hours': ten99Rates?.charge_max_daily_regular_hours,
    '1099 charge_max_daily_overtime_hours': ten99Rates?.charge_max_daily_overtime_hours,
    '1099 charge_overtime_multiplier': ten99Rates?.charge_overtime_multiplier,
    '1099 charge_double_overtime_multiplier': ten99Rates?.charge_double_overtime_multiplier,
    '1099 charge_max_weekly_regular_hours': ten99?.charge_max_weekly_regular_hours,
    '1099 charge_seventh_day_rule_enabled': ten99?.charge_seventh_day_rule_enabled,
    // 1099 Payout settings
    '1099 payout_max_daily_regular_hours': ten99Rates?.payout_max_daily_regular_hours,
    '1099 payout_max_daily_overtime_hours': ten99Rates?.payout_max_daily_overtime_hours,
    '1099 payout_overtime_multiplier': ten99Rates?.payout_overtime_multiplier,
    '1099 payout_double_overtime_multiplier': ten99Rates?.payout_double_overtime_multiplier,
    '1099 payout_max_weekly_regular_hours': ten99?.payout_max_weekly_regular_hours,
    '1099 payout_seventh_day_rule_enabled': ten99?.payout_seventh_day_rule_enabled,
    // W2 settings
    'W2 Charge Overtime Multiplier': w2Rates?.charge_overtime_multiplier,
    'W2 Charge Double Time Multiplier': w2Rates?.charge_double_overtime_multiplier,
    'W2 Payout Overtime Multiplier': w2Rates?.payout_overtime_multiplier,
    'W2 Payout Double Time Multiplier': w2Rates?.payout_double_overtime_multiplier,
    'Margin Percent W2 Travel': w2?.travel?.margin_percent_w2_travel,
    'Margin Percent W2 TCV': w2?.travel?.margin_percent_w2_tcv,
    'Margin W2 TCV Cents': w2?.travel?.margin_w2_tcv_cents,
    // Assignment settings
    'W2 Assignment Bonus Tier2 Travel Amount Cents':
      w2?.travel?.assignment_bonus_tier2_travel_amount_cents,
    'W2 Assignment Bonus Tier2 Travel Amount TCV Cents':
      w2?.travel?.assignment_bonus_tier2_travel_amount_tcv_cents,
    'W2 Assignment Bonus Tier1 Travel Amount Cents':
      w2?.travel?.assignment_bonus_tier1_travel_amount_cents,
    'W2 Assignment Bonus Tier1 Travel Amount TCV Cents':
      w2?.travel?.assignment_bonus_tier1_travel_amount_tcv_cents,
    'Total Margin Percent W2': w2?.local?.total_margin_percent_w2,
  });

  return rateSettingToExport;
}

export const buildCustomExporter = (
  rateSettings,
  filenamePrefix: string,
  isMarket: boolean,
  settingsNestedOperator: string,
) => {
  const rateSettingsToExport = rateSettings.map((aRateSetting) =>
    mapRateSettingToRow(aRateSetting, isMarket, settingsNestedOperator),
  );

  return jsonExport(rateSettingsToExport, {}, (_, csv) =>
    downloadCSV(csv, `${filenamePrefix} Position Rate Settings`),
  );
};
